'use client';

import type { PersonalLoanCalculationRequest } from '@hyphengroup/cms-components/calculatedAttributes/collect';
import {
  CalculationScope,
  CalculationStoreProvider,
  type CalculationStore,
  type LoanTerms,
} from '@hyphengroup/cms-components/calculatedAttributes/store';
import type { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import type { VerticalType } from '@hyphengroup/cms-components/types/cmsTypes';
import { useSafeSearchParamsExtended } from '@hyphengroup/cms-components/utils/useSafeSearchParams';
import { useMemo, type ReactNode } from 'react';
import {
  applyCalculationScopeId,
  collectApplyCalculatedAttributes,
} from '../lib/calculatedAttributes';
import {
  useLoanRecalculation,
  type LoanCalculationJob,
  type LoanCalculations,
} from '$/(cms)/_modules/loanScope/useLoanRecalculation';
import { useClientContext } from '$/modules/context/client';

/**
 * Fills the calculation store for the apply form and keeps it in step with the
 * loan it is given (CMS-1959).
 *
 * `serverStore` is optional because the modal has none: it fetches its product
 * on the client, and the recalculation fills the slot either way.
 */
export const ApplyCalculationsProvider = ({
  product,
  vertical,
  terms,
  serverStore,
  isDraft,
  children,
}: {
  product: CmsProduct | undefined;
  vertical: VerticalType | undefined;
  /** The loan the form prices — `useApplyLoan` decides it with what the form sends. */
  terms: LoanTerms;
  /** What the server calculated with the defaults, where a server rendered it. */
  serverStore?: CalculationStore;
  isDraft: boolean;
  children: ReactNode;
}) => {
  const { brand } = useClientContext();

  const loanRequest = useMemo(
    () =>
      collectApplyCalculatedAttributes(product, vertical, brand).find(
        (request): request is PersonalLoanCalculationRequest =>
          request.provider === 'PL_CALCULATOR',
      ),
    [product, vertical, brand],
  );

  const ready = !!useSafeSearchParamsExtended()?.searchParamsReady;

  const scopeId = product ? applyCalculationScopeId(product) : undefined;
  const fromServer = scopeId ? serverStore?.[scopeId]?.personalLoan : undefined;

  const jobs = useMemo<LoanCalculationJob[]>(
    () =>
      loanRequest
        ? [
            {
              moduleId: loanRequest.moduleId,
              productIds: loanRequest.criteria.productIds,
              terms,
            },
          ]
        : [],
    [loanRequest, terms],
  );

  const initial = useMemo<LoanCalculations>(
    () =>
      fromServer?.calculations && scopeId
        ? {
            byModule: { [scopeId]: fromServer.calculations },
            termsByModule: { [scopeId]: fromServer.terms },
          }
        : { byModule: {}, termsByModule: {} },
    [fromServer, scopeId],
  );

  const { byModule, pending } = useLoanRecalculation({
    jobs,
    initial,
    isDraft,
    enabled: ready,
  });

  const store = useMemo<CalculationStore>(() => {
    if (!scopeId) return {};
    return {
      [scopeId]: {
        // The product's vertical: the engine judges the form's attributes the
        // way it judges a block's (CMS-2052).
        vertical,
        // The slot exists as soon as the form asks, so a first load with no
        // figures yet reads as pending rather than as "not ours".
        personalLoan: loanRequest
          ? {
              terms,
              calculations: byModule[scopeId],
              pending,
            }
          : undefined,
      },
    };
  }, [scopeId, vertical, loanRequest, byModule, pending, terms]);

  if (!scopeId) return <>{children}</>;

  return (
    <CalculationStoreProvider value={store}>
      {/* One product, one scope: every surface on the form reads the same
          slot without wiring of its own. */}
      <CalculationScope moduleId={scopeId}>{children}</CalculationScope>
    </CalculationStoreProvider>
  );
};
